import { useCallback, useEffect, useRef, useState } from 'react'
import { clearSessionLocked } from './sessionLockStorage'

export type InactivityStatus = 'ativo' | 'aviso' | 'bloqueado'

const LAST_ACTIVITY_STORAGE_KEY = 'kt-auth-last-activity'
const ACTIVITY_REGISTER_THROTTLE_MS = 10000
const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'wheel']
const TICK_MS = 1000

type UseInactivityTimerParams = {
  timeoutMs: number
  warningWindowMs: number
  enabled: boolean
}

type UseInactivityTimerResult = {
  status: InactivityStatus
  remainingSeconds: number
  unlock: () => void
}

/**
 * Monitora atividade do usuário via localStorage (compartilhado entre abas) e
 * transiciona ativo -> aviso -> bloqueado conforme o tempo de inatividade
 * configurado. Uma vez bloqueado, atividade é ignorada: só `unlock()` (chamado
 * após reautenticação bem-sucedida) retoma a contagem.
 */
export function useInactivityTimer({
  timeoutMs,
  warningWindowMs,
  enabled,
}: UseInactivityTimerParams): UseInactivityTimerResult {
  const [status, setStatus] = useState<InactivityStatus>('ativo')
  const [remainingSeconds, setRemainingSeconds] = useState(0)
  const statusRef = useRef<InactivityStatus>('ativo')

  const setStatusSync = useCallback((next: InactivityStatus) => {
    statusRef.current = next
    setStatus(next)
  }, [])

  const unlock = useCallback(() => {
    clearSessionLocked()
    localStorage.setItem(LAST_ACTIVITY_STORAGE_KEY, Date.now().toString())
    setRemainingSeconds(0)
    setStatusSync('ativo')
  }, [setStatusSync])

  useEffect(() => {
    if (!enabled || !timeoutMs || timeoutMs <= 0) {
      setStatusSync('ativo')
      return
    }

    let lastRegistered = 0
    const registerActivity = () => {
      if (statusRef.current === 'bloqueado') return

      const now = Date.now()
      if (now - lastRegistered < ACTIVITY_REGISTER_THROTTLE_MS) return
      lastRegistered = now
      localStorage.setItem(LAST_ACTIVITY_STORAGE_KEY, now.toString())

      if (statusRef.current !== 'ativo') {
        setRemainingSeconds(Math.ceil(timeoutMs / 1000))
        setStatusSync('ativo')
      }
    }

    registerActivity()
    setRemainingSeconds(Math.ceil(timeoutMs / 1000))
    ACTIVITY_EVENTS.forEach((event) =>
      window.addEventListener(event, registerActivity, { passive: true })
    )

    const intervalId = window.setInterval(() => {
      if (statusRef.current === 'bloqueado') return

      const lastActivity = Number(localStorage.getItem(LAST_ACTIVITY_STORAGE_KEY)) || Date.now()
      const remainingMs = timeoutMs - (Date.now() - lastActivity)

      setRemainingSeconds(Math.max(0, Math.ceil(remainingMs / 1000)))

      if (remainingMs <= 0) {
        setStatusSync('bloqueado')
      } else if (remainingMs <= warningWindowMs) {
        setStatusSync('aviso')
      } else if (statusRef.current !== 'ativo') {
        setStatusSync('ativo')
      }
    }, TICK_MS)

    return () => {
      ACTIVITY_EVENTS.forEach((event) => window.removeEventListener(event, registerActivity))
      window.clearInterval(intervalId)
      localStorage.removeItem(LAST_ACTIVITY_STORAGE_KEY)
    }
  }, [enabled, timeoutMs, warningWindowMs, setStatusSync])

  return { status, remainingSeconds, unlock }
}
